// src/features/system/devices/hooks/useDeviceValidation.ts
// ────────────────────────────────────────────────────────────────────────────
// Hook-owns-all-state for the Device Validation feature.
// Provides: history query, snapshot query, start mutation, submit mutation.
// ────────────────────────────────────────────────────────────────────────────
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  startValidationApi,
  fetchValidationHistoryApi,
  fetchValidationSnapshotApi,
  submitValidationResultApi,
  VALIDATION_KEYS,
  type StartValidationRequest,
  type ValidationOutcomeRow,
  type ValidationHotEvent,
  type ValidationResult,
} from '../api/deviceValidationApi';

export function useDeviceValidation(deviceId: string | null) {
  const qc = useQueryClient();

  // ── Validation history (outcomes) ──────────────────────────────────────

  const history = useQuery<ValidationOutcomeRow[], Error>({
    queryKey:  VALIDATION_KEYS.history(deviceId ?? ''),
    queryFn:   () => fetchValidationHistoryApi(deviceId!),
    enabled:   !!deviceId,
    staleTime: 30_000,
  });

  // ── Real-time hot-stream snapshot (auto-refresh while running) ─────────

  const snapshot = useQuery<ValidationHotEvent[], Error>({
    queryKey:        VALIDATION_KEYS.snapshot(deviceId ?? ''),
    queryFn:         () => fetchValidationSnapshotApi(deviceId!),
    enabled:         !!deviceId,
    staleTime:       5_000,
    refetchInterval: 10_000,
  });

  // ── Start a validation run ─────────────────────────────────────────────

  const start = useMutation({
    mutationFn: (body: StartValidationRequest) => startValidationApi(body),
    onSuccess:  () => {
      if (deviceId) {
        qc.invalidateQueries({ queryKey: VALIDATION_KEYS.history(deviceId) });
        qc.invalidateQueries({ queryKey: VALIDATION_KEYS.snapshot(deviceId) });
      }
    },
  });

  // ── Submit a completed result (local validation) ───────────────────────

  const submitResult = useMutation({
    mutationFn: (result: ValidationResult) => submitValidationResultApi(result),
    onSuccess:  () => {
      if (deviceId) {
        qc.invalidateQueries({ queryKey: VALIDATION_KEYS.history(deviceId) });
        qc.invalidateQueries({ queryKey: VALIDATION_KEYS.snapshot(deviceId) });
      }
    },
  });

  return {
    history,
    snapshot,
    start,
    submitResult,
  };
}
